import React, { useEffect, useState } from "react";
import Modal from "antd/es/modal/Modal";
import { toast } from "react-toastify";

/**
 * @param {{
 *  open: boolean;
 *  editing?: { id?: string; nombre?: string; descripcion?: string; monto?: number; activo?: boolean } | null;
 *  saving?: boolean;
 *  onClose: () => void;
 *  onSubmit: (payload: { nombre: string; descripcion: string; monto: number; activo: boolean }) => void;
 * }} props
 */
export function ModalProductos({ open, editing, saving = false, onClose, onSubmit }) {
  const isEditing = !!editing;
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [monto, setMonto] = useState("");
  const [activo, setActivo] = useState(true);

  // Cargar/limpiar estado al abrir o cambiar "editing"
  useEffect(() => {
    if (editing) {
      setNombre(editing.nombre || "");
      setDescripcion(editing.descripcion || "");
      setMonto(editing.monto?.toString() || "");
      setActivo(editing.activo ?? true);
    } else {
      // Al crear nuevo, siempre activo
      setNombre("");
      setDescripcion("");
      setMonto("");
      setActivo(true);
    }
  }, [editing, open]);

  function submit() {
    if (!nombre.trim()) {
      toast.error("El nombre del producto es obligatorio");
      return;
    }
    
    const montoNum = parseFloat(monto);
    if (isNaN(montoNum) || montoNum < 0) {
      toast.error("El monto debe ser un número válido mayor o igual a 0");
      return;
    }

    // Reglas: Al crear, SIEMPRE activo (aunque el switch esté en falso)
    const finalActivo = isEditing ? activo : true;

    const payload = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim(),
      monto: montoNum,
      activo: finalActivo,
    };
    onSubmit(payload);
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold">
            {isEditing ? "Editar producto" : "Nuevo producto"}
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 transition disabled:opacity-60"
            disabled={saving}
          >
            {saving ? "Guardando..." : isEditing ? "Guardar" : "Crear"}
          </button>
        </div>
      }
      centered
      width={600}
      destroyOnClose
      maskClosable={false}
      keyboard
      styles={{
        content: { borderRadius: 16 },
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: 8,
        },
      }}
    >
      <div className="space-y-5">
        {/* Datos base */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nombre */}
          <div className="md:col-span-2">
            <label
              htmlFor="producto-nombre"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              id="producto-nombre"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
              placeholder="ej: Mensualidad, Uniforme, Inscripción"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </div>

          {/* Monto */}
          <div>
            <label
              htmlFor="producto-monto"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Monto <span className="text-red-500">*</span>
            </label>
            <input
              id="producto-monto"
              type="number"
              step="0.01"
              min="0"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
              placeholder="0.00"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              disabled={saving}
            />
          </div>

          {/* Estado */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Estado
            </label>
            <label
              className={
                "inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border " +
                (isEditing
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-70") +
                " border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              }
              title={
                isEditing
                  ? "Puedes activar o inactivar el producto"
                  : "Al crear, el producto se establece activo automáticamente"
              }
            >
              <input
                type="checkbox"
                className="peer sr-only"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
                disabled={!isEditing || saving}
              />
              <span
                className={
                  "w-10 h-6 rounded-full transition-all relative " +
                  (activo ? "bg-emerald-500" : "bg-slate-300")
                }
              >
                <span
                  className={
                    "absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform " +
                    (activo ? "translate-x-4" : "translate-x-0")
                  }
                />
              </span>
              <span className="font-medium">
                {activo ? "Activo" : "Inactivo"}
              </span>
            </label>
          </div>

          {/* Descripción */}
          <div className="md:col-span-2">
            <label
              htmlFor="producto-descripcion"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Descripción
            </label>
            <textarea
              id="producto-descripcion"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
              rows={3}
              placeholder="Descripción del producto"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              disabled={saving}
            />
          </div>
        </section>
      </div>
    </Modal>
  );
}
